
import * as _ from "lodash"
import * as am4core from "@amcharts/amcharts4/core"
import * as am4charts from "@amcharts/amcharts4/charts"
import Vue from "vue"
import {Inject, Prop} from "vue-property-decorator"
import Component from "vue-class-component"
import {theme} from "@/theme"
import {dropShadowFilter} from "@/ui/chart/dropShadowFilter"
import {MetricSliced, MetricStore} from "@/cube/metric-data/MetricStore"
import {
  MemberSnapshotCube,
  MemberSnapshotMeasure,
  SuperScoreCube,
  SuperScoreDimension,
} from "@/cube"
import {keyedDataUpdate} from "@/cube/keyedDataUpdate"
import {toKey} from "@/cube/toKey"
import {removeDimensionSortPrefix} from "@/cube/removeDimensionSortPrefix"
import {toPercent} from "@/cube/metric-data/toPercent"
import {dropSortPrefixAdapter} from "@/ui/chart/dropSortPrefixAdapter"
import {createChangeRateLabel} from "@/ui/chart/updateChangeRate"
import NoDataMessage from "@/ui/NoDataMessage.vue"
import {dimensionSettings} from "@/cube/settings"
import {AsfaVariables, CalculationVariableService} from "@/CalculationVariableService"

@Component({
  components: {NoDataMessage},
})
export default class RetirementAdequacyChart extends Vue {
  @Inject()
  calculationVariableService: CalculationVariableService

  @Prop()
  metricStore: MetricStore

  adequacyValues: AsfaVariables

  private hasData = true
  private ready = false

  async created() {
    this.adequacyValues = (await this.calculationVariableService.get())!.asfaRetirement
  }

  mounted() {
    const metricData = this.metricStore.getSliced(MemberSnapshotMeasure.countMembers, [
      SuperScoreDimension.projectedRetirementStandardName,
    ])

    let chart: am4charts.PieChart | undefined
    let labels: {titleLabel: am4core.Label; valueLabel: am4core.Label}

    if (metricData.end && metricData.end.length !== 0) {
      chart = this.createChart(metricData)
      labels = this.createMiddleLabels(chart)
    }

    this.$watch(
      () => metricData.end,
      () => {
        if (metricData.end) {
          if (chart === undefined) {
            chart = this.createChart(metricData)
            labels = this.createMiddleLabels(chart)
          }
          this.hasData = metricData.end.length > 0
          if (this.hasData) {
            chart.show()
            this.updateChartData(chart, labels, metricData)
            this.ready = true
          } else {
            chart.hide()
          }
        }
      },
      {immediate: true}
    )

    this.$watch(
      () => metricData.dateRange,
      () => {
        chart?.legend.invalidateData()
      }
    )

    this.$on("hook:beforeDestroy", () => {
      chart?.dispose()
    })
  }

  private createChart(metricData: MetricSliced<MemberSnapshotMeasure.countMembers>) {
    const chart = am4core.create((<any>this.$refs).chart, am4charts.PieChart)
    chart.responsive.enabled = true
    chart.paddingTop = 2.5

    chart.radius = am4core.percent(75)
    chart.innerRadius = am4core.percent(chart.radius.percent - 16)
    chart.startAngle = 180
    chart.endAngle = 360

    const series = chart.series.push(new am4charts.PieSeries())
    series.dataFields.value = MemberSnapshotCube.countMembers
    series.dataFields.category = SuperScoreCube.projectedRetirementStandardName
    series.filters.push(dropShadowFilter())
    series.hiddenState.properties.startAngle = 90
    series.hiddenState.properties.endAngle = 90
    series.alignLabels = false
    series.labels.template.text = "{value.percent.formatNumber('###.')}%"
    series.labels.template.fill = am4core.color(theme().semiMutedColor)

    const sliceTemplate = series.slices.template
    sliceTemplate.strokeWidth = 2
    sliceTemplate.stroke = am4core.color("white")
    sliceTemplate.fillOpacity = 0.35
    sliceTemplate.adapter.add("fill", (value, target) =>
      chartColorForItem(<am4charts.PieSeriesDataItem>target.dataItem)
    )
    sliceTemplate.adapter.add("stroke", (value, target) =>
      chartColorForItem(<am4charts.PieSeriesDataItem>target.dataItem)
    )
    sliceTemplate.adapter.add("tooltipText", dropSortPrefixAdapter)
    sliceTemplate.inert = true

    this.addLegend(chart, metricData)

    return chart
  }

  private getLegendSuffix(retirementStandard: string) {
    const modestSingleYearTotal = Vue.filter("money").call(
      undefined,
      this.adequacyValues.modestSingleYearTotal
    )
    const comfortableSingleYearTotal = Vue.filter("money").call(
      undefined,
      this.adequacyValues.comfortableSingleYearTotal
    )
    switch (retirementStandard) {
      case "1|Inadequate":
        return ""
      case "2|Modest":
        return modestSingleYearTotal
      case "3|Comfortable":
        return comfortableSingleYearTotal
      case "4|Aspirational":
        return "60% of income"
      default:
        return ""
    }
  }

  private addLegend(
    chart: am4charts.PieChart,
    metricData: MetricSliced<MemberSnapshotMeasure.countMembers>
  ) {
    const legend = new am4charts.Legend()
    // Need to set the legend before setting the labels template
    chart.legend = legend

    legend.marginTop = -5
    legend.labels.template.text = ""

    const labelsTemplate = legend.valueLabels.template
    labelsTemplate.adapter.add("html", (value, target) => {
      const dataItem = <am4charts.PieSeriesDataItem | undefined>target.dataItem
      if (dataItem === undefined || !dataItem.hasProperties) return undefined

      const category = dataItem.category
      const itemChange = metricData.changeRateByKey[category]
      return (
        removeDimensionSortPrefix(category) +
        createChangeRateLabel(
          SuperScoreCube.projectedRetirementStandardName,
          removeDimensionSortPrefix(category),
          {
            // FIXME should this return zero or NA in this scenario?
            value: itemChange?.changeRate ?? 0,
            unit: "%",
          }
        ) +
        wrapAsfa(this.getLegendSuffix(category))
      )
    })
    labelsTemplate.fontWeight = "100"
    labelsTemplate.paddingLeft = -1
    labelsTemplate.textAlign = "start"

    legend.itemContainers.template.paddingLeft = 3
  }

  private createMiddleLabels(chart: am4charts.PieChart) {
    const titleLabel = chart.seriesContainer.createChild(am4core.Label)
    titleLabel.hidden = true
    titleLabel.text = "Comfortable"
    titleLabel.horizontalCenter = "middle"
    titleLabel.verticalCenter = "middle"

    const valueLabel = chart.seriesContainer.createChild(am4core.Label)
    valueLabel.hidden = true
    valueLabel.horizontalCenter = "middle"
    valueLabel.verticalCenter = "middle"
    // FIXME do not hard code fonts
    valueLabel.fontFamily = "corpid-caps, gotham narrow"

    const series = chart.series.getIndex(0)!

    // Show the label only after the data is displayed in the series
    series.events.once(series.showOnInit ? "appeared" : "ready", () => {
      titleLabel.show()
      valueLabel.show()
    })

    series.events.on("sizechanged", () => {
      if (series.measuredWidth < 200) {
        titleLabel.fontSize = 0
        valueLabel.fontSize = 0
      } else if (series.measuredWidth < 260) {
        const paddingBase = 45
        titleLabel.fontSize = "0.8rem"
        titleLabel.paddingBottom = paddingBase + 50
        valueLabel.fontSize = "1.6rem"
        valueLabel.paddingBottom = paddingBase
      } else if (series.measuredWidth < 320) {
        const paddingBase = 65
        titleLabel.fontSize = "1rem"
        titleLabel.paddingBottom = paddingBase + 60
        valueLabel.fontSize = "2rem"
        valueLabel.paddingBottom = paddingBase
      } else {
        const paddingBase = 85
        titleLabel.fontSize = "1.4rem"
        titleLabel.paddingBottom = paddingBase + 70
        valueLabel.fontSize = "2.5rem"
        valueLabel.paddingBottom = paddingBase
      }
    })

    return {titleLabel, valueLabel}
  }

  private updateChartData(
    chart: am4charts.PieChart,
    middleLabels: {titleLabel: am4core.Label; valueLabel: am4core.Label},
    metricData: MetricSliced<MemberSnapshotMeasure.countMembers>
  ) {
    if (chart.data.length === 0) {
      chart.data = <Array<any>>metricData.end
      chart.invalidateData()
    } else {
      keyedDataUpdate(
        metricData.metric,
        (row: any) => toKey(row, [SuperScoreCube.projectedRetirementStandardName]),
        metricData.end!,
        chart.data
      )
      chart.invalidateRawData()
    }

    const comfortablePct = _.round(
      toPercent(metricData, {
        [SuperScoreCube.projectedRetirementStandardName]: "3|Comfortable",
      }) * 100
    )

    middleLabels.valueLabel.text = `${comfortablePct}%`
  }
}

function getColor(retirementStandard: string) {
  const {color} = dimensionSettings(
    SuperScoreDimension.projectedRetirementStandardName,
    retirementStandard
  )
  if (color === undefined) throw Error(`No color defined for ${retirementStandard}`)
  else return color
}

function wrapAsfa(amount: string) {
  return `<div style="margin-top: -0.35em">${amount}</div>`
}

function chartColorForItem(dataItem: am4charts.PieSeriesDataItem) {
  return am4core.color(getColor(removeDimensionSortPrefix(dataItem.category)))
}
